'use client';

import Image from 'next/image';
import { AppDownload } from '../AppDownload/AppDownload';
import { LandingPageForm } from '../LandingPageForm/LandingPageForm';
import { JourneyType } from '../LandingPageForm/LandingPageForm';

type Layout = 'default' | 'split';

interface AppDownloadWithFormProps {
  layout?: Layout;

  backgroundImage?: string;
  backgroundAlt?: string;

  formHeading?: string;
  formSubheading?: string;

  heading?: string;
  subheading?: string;
  mobileButtonText?: string;
  downloadUrl: string;
  qrCodeImage?: string;
  mobileButtonAriaLabel?: string;
  appleStoreAriaLabel?: string;
  googlePlayAriaLabel?: string;

  regLabel?: string;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  useCardStyle?: boolean;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
  journey?: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker?: boolean;
}

export const APP_DOWNLOAD_WITH_FORM_COMPONENT_NAME = 'AppDownloadWithForm';

const BackgroundImage = ({ backgroundImage, backgroundAlt }: { backgroundImage?: string; backgroundAlt: string }) => {
  if (!backgroundImage) return null;

  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
      <Image
        src={backgroundImage}
        alt={backgroundAlt}
        fill
        className="object-cover"
        sizes="(min-width: 768px) 100vw, 0px"
      />
    </div>
  );
};

type FormSectionProps = {
  formHeading?: string;
  formSubheading?: string;
  containerClassName?: string;
  regLabel: string;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  useCardStyle: boolean;
  formIntroText?: string;
  submitButtonText: string;
  submitButtonColor: 'orange' | 'purple';
  journey: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker: boolean;
};

const FormSection = ({
  formHeading,
  formSubheading,
  containerClassName = 'pb-8',
  regLabel,
  defaultDurationType,
  daysOnly,
  useBanner,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  useCardStyle,
  formIntroText,
  submitButtonText,
  submitButtonColor,
  journey,
  regPlateOnly,
  showDurationPicker,
}: FormSectionProps) => (
  <div className={`tc-container ${containerClassName}`}>
    {(formHeading || formSubheading) && (
      <header className="mb-6 text-left md:text-center">
        {formHeading && (
          <h2 className="text-dark mb-4 text-[34px] leading-[120%] font-black text-balance md:text-[40px] lg:text-[44px]">
            {formHeading}
          </h2>
        )}
        {formSubheading && (
          <p className="text-dark text-[18px] leading-[120%] font-bold md:text-[23px]">{formSubheading}</p>
        )}
      </header>
    )}
    <LandingPageForm
      regLabel={regLabel}
      defaultDurationType={defaultDurationType}
      daysOnly={daysOnly}
      useBanner={useBanner}
      bannerCoverType={bannerCoverType}
      bannerCoverDuration={bannerCoverDuration}
      checkboxLabel={checkboxLabel}
      useCardStyle={useCardStyle}
      formIntroText={formIntroText}
      submitButtonText={submitButtonText}
      submitButtonColor={submitButtonColor}
      journey={journey}
      regPlateOnly={regPlateOnly}
      showDurationPicker={showDurationPicker}
    />
  </div>
);

export const AppDownloadWithForm = ({
  layout = 'default',

  backgroundImage,
  backgroundAlt = 'Background image',

  formHeading,
  formSubheading,

  heading,
  subheading,
  mobileButtonText,
  downloadUrl,
  qrCodeImage,
  mobileButtonAriaLabel,
  appleStoreAriaLabel,
  googlePlayAriaLabel,

  regLabel = 'Enter Registration Number',
  defaultDurationType,
  daysOnly,
  useBanner,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  useCardStyle = true,
  formIntroText,
  submitButtonText = 'Continue without discount',
  submitButtonColor = 'purple',
  journey,
  regPlateOnly,
  showDurationPicker = false,
}: AppDownloadWithFormProps) => {
  const formProps = {
    regLabel,
    defaultDurationType,
    daysOnly,
    useBanner,
    bannerCoverType,
    bannerCoverDuration,
    checkboxLabel,
    useCardStyle,
    formIntroText,
    submitButtonText,
    submitButtonColor,
    journey: journey || ('none' as JourneyType),
    regPlateOnly,
    showDurationPicker,
  };

  if (layout === 'default') {
    return (
      <section className="relative w-full">
        <BackgroundImage backgroundImage={backgroundImage} backgroundAlt={backgroundAlt} />

        <div className="relative">
          <AppDownload
            heading={heading}
            headingLevel="h1"
            subheading={subheading}
            mobileButtonText={mobileButtonText}
            downloadUrl={downloadUrl}
            qrCodeImage={qrCodeImage}
            mobileButtonAriaLabel={mobileButtonAriaLabel}
            appleStoreAriaLabel={appleStoreAriaLabel}
            googlePlayAriaLabel={googlePlayAriaLabel}
          />

          <FormSection {...formProps} />
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full">
      <BackgroundImage backgroundImage={backgroundImage} backgroundAlt={backgroundAlt} />

      <div className="relative">
        <FormSection
          formHeading={formHeading}
          formSubheading={formSubheading}
          containerClassName="pb-0"
          {...formProps}
        />

        <AppDownload
          heading={heading}
          headingLevel="h2"
          subheading={subheading}
          mobileButtonText={mobileButtonText}
          downloadUrl={downloadUrl}
          qrCodeImage={qrCodeImage}
          mobileButtonAriaLabel={mobileButtonAriaLabel}
          appleStoreAriaLabel={appleStoreAriaLabel}
          googlePlayAriaLabel={googlePlayAriaLabel}
        />
      </div>
    </section>
  );
};
